import { useEffect, useRef, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../Components/context/auth-context';
import {
  bidActions,
  readStoredBid,
  writeStoredBid,
  clearStoredBid,
  toBidSnapshot,
} from '../store/bid';
import BidsAPI from '../services/api/bids/bids-api';
import { canAccessBids } from '../utils/bidsAccess';
import Logger from '../utils/logger';

/**
 * Rehydrates the selected bid once after authentication resolves (ticket #295).
 *
 * Unlike the project, the stored bid is dispatched straight away so the sidebar
 * block doesn't flash empty, then re-fetched in the background: fresh data
 * replaces the snapshot (name or number may have changed), and a 404 / 403
 * (bid gone, or access lost) clears it quietly. Other errors (network, 500)
 * keep the stored bid — it will be checked again next session.
 */
export const useBidRehydration = () => {
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const { token, userId } = auth;
  const hasAccess = canAccessBids(auth);

  // Guard so rehydration runs only once per authenticated session.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!hasAccess || !token || !userId || hasRunRef.current) return;
    hasRunRef.current = true;

    const stored = readStoredBid(userId);
    if (!stored || !stored.id) return;

    dispatch(bidActions.selectBid(stored));

    let cancelled = false;

    const refresh = async () => {
      try {
        const bid = await BidsAPI.get(stored.id, token);
        if (cancelled) return;
        const snapshot = toBidSnapshot(bid);
        dispatch(bidActions.selectBid(snapshot));
        writeStoredBid(userId, snapshot);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404 || status === 403) {
          Logger.info('Stored bid no longer available — clearing', {
            storedBidId: stored.id,
            status,
          });
          clearStoredBid(userId);
          dispatch(bidActions.clearBid());
        } else {
          Logger.error('Failed to refresh stored bid — keeping it', err);
        }
      }
    };

    refresh();

    return () => {
      cancelled = true;
    };
  }, [hasAccess, token, userId, dispatch]);
};

export default useBidRehydration;
